import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/primitives/Button";
import { cn } from "@/lib/cn";
import { useSession } from "@/session/SessionProvider";
import { MeshBackdrop } from "./MeshBackdrop";
import { SiteFooter } from "./SiteFooter";

export type AppShellMode = "public" | "workspace" | "map";

type Props = {
	mode: AppShellMode;
	children: ReactNode;
	mapBar?: ReactNode;
};

function shellNavClass({ isActive }: { isActive: boolean }) {
	return cn(
		"rounded-[var(--radius-control)] px-2.5 py-1 text-[13px]",
		isActive
			? "bg-zinc-950/[0.06] font-medium text-fg dark:bg-white/10"
			: "text-fg-muted hover:bg-zinc-950/[0.04] hover:text-fg dark:hover:bg-white/[0.06]",
	);
}

export function AppShell({ mode, children, mapBar }: Props) {
	const { user, isAuthed, logout } = useSession();
	const showFooter = mode === "public";
	const showMesh = mode === "public" || mode === "workspace";

	return (
		<div className="relative flex min-h-svh flex-col bg-surface">
			{showMesh ? <MeshBackdrop /> : null}
			{mode === "map" && mapBar ? (
				mapBar
			) : (
				<header className="relative z-20 flex h-12 shrink-0 items-center justify-between gap-3 border-b border-zinc-950/5 bg-surface/80 px-3 backdrop-blur-md dark:border-white/5 sm:px-4">
					<div className="flex min-w-0 items-center gap-3">
						<Link
							to={isAuthed ? "/app" : "/"}
							className="shrink-0 text-[15px] font-semibold tracking-tight text-fg"
						>
							Kartenwerk
						</Link>
						{isAuthed && mode === "workspace" ? (
							<nav className="flex items-center gap-0.5 text-sm">
								<NavLink to="/app" className={shellNavClass}>
									Karten
								</NavLink>
								<NavLink to="/account" className={shellNavClass}>
									Konto
								</NavLink>
								{user?.isAdmin ? (
									<NavLink to="/invites" className={shellNavClass}>
										Einladungen
									</NavLink>
								) : null}
							</nav>
						) : null}
					</div>
					<div className="flex shrink-0 items-center gap-2 text-sm">
						{isAuthed ? (
							<>
								<span className="hidden max-w-[12rem] truncate text-[13px] text-fg-muted sm:inline">
									{user?.email}
								</span>
								<Button variant="ghost" size="sm" onClick={() => void logout()}>
									Abmelden
								</Button>
							</>
						) : (
							<Button as="a" href="/login" variant="ghost" size="sm">
								Anmelden
							</Button>
						)}
					</div>
				</header>
			)}
			<main
				className={cn(
					"relative z-10 flex min-h-0 flex-1 flex-col",
					mode === "map" && "min-h-svh",
				)}
			>
				{children}
			</main>
			{showFooter ? <SiteFooter /> : null}
		</div>
	);
}
